import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import Select from "react-select";
import * as Api from "../../../General/api";
import * as Common from "../../../General/common";

const fieldsetClass = "p-2 mb-2 bg-light border border-dark rounded";

/**
 * Facility Information tab — Section 2.1 + layout aligned with main Add/Edit Facility (fieldset sections, label-above fields).
 * Read-only: ownership, phone, fax, CMN specialist, last sync.
 * Editable: clinician name, alternate signees (PECOS physician dropdown), notes, DocuSign,
 *            delivery method (from DB), frequency (from DB), medical director fields.
 */
function FacilityInfoTab({ facilityId }) {
  const [info, setInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [physicianOptions, setPhysicianOptions] = useState([]);
  const [deliveryMethodOptions, setDeliveryMethodOptions] = useState([]);
  const [frequencyOptions, setFrequencyOptions] = useState([]);

  const [notes, setNotes] = useState("");
  const [docuSignName, setDocuSignName] = useState("");
  const [docuSignEmail, setDocuSignEmail] = useState("");
  const [deliveryMethodId, setDeliveryMethodId] = useState("");
  const [frequencyId, setFrequencyId] = useState("");
  const [alternateSignee1, setAlternateSignee1] = useState(null);
  const [alternateSignee2, setAlternateSignee2] = useState(null);
  const [clinicianFirstName, setClinicianFirstName] = useState("");
  const [clinicianLastName, setClinicianLastName] = useState("");
  const [directorName, setDirectorName] = useState("");
  const [directorEmail, setDirectorEmail] = useState("");
  const [directorPhone, setDirectorPhone] = useState("");
  const [directorFax, setDirectorFax] = useState("");

  useEffect(() => {
    Api.getRequest(Api.Uri_CmnLookupPhysicians)
      .then((res) => {
        if (res.data.statusCode === 200 && Array.isArray(res.data.result)) {
          setPhysicianOptions(res.data.result.map((p) => ({ value: p.id, label: p.name })));
        }
      })
      .catch((ex) => Common.LogError(ex, "FacilityInfoTab", "loadPhysicians"));

    Api.getRequest(Api.Uri_CmnLookupDeliveryMethods)
      .then((res) => {
        if (res.data.statusCode === 200 && Array.isArray(res.data.result)) {
          setDeliveryMethodOptions(res.data.result.map((m) => ({ value: String(m.id), label: m.name })));
        }
      })
      .catch((ex) => Common.LogError(ex, "FacilityInfoTab", "loadDeliveryMethods"));

    Api.getRequest(Api.Uri_CmnLookupFrequencies)
      .then((res) => {
        if (res.data.statusCode === 200 && Array.isArray(res.data.result)) {
          setFrequencyOptions(res.data.result.map((f) => ({ value: String(f.id), label: f.name })));
        }
      })
      .catch((ex) => Common.LogError(ex, "FacilityInfoTab", "loadFrequencies"));
  }, []);

  useEffect(() => {
    if (!facilityId) return;
    setIsLoading(true);
    Api.getRequest(Api.Uri_CmnFacilityInfo + facilityId)
      .then((res) => {
        if (res.data.statusCode === 200 && res.data.result) {
          const d = res.data.result;
          setInfo(d);
          setNotes(d.notes || "");
          setDocuSignName(d.docuSignName || "");
          setDocuSignEmail(d.docuSignEmail || "");
          setDeliveryMethodId(d.deliveryMethodID != null ? String(d.deliveryMethodID) : "");
          setFrequencyId(d.frequencyID != null ? String(d.frequencyID) : "");
          setAlternateSignee1(d.alternateSignee1 != null ? d.alternateSignee1 : null);
          setAlternateSignee2(d.alternateSignee2 != null ? d.alternateSignee2 : null);
          setClinicianFirstName(d.clinicianFirstName || "");
          setClinicianLastName(d.clinicianLastName || "");
          setDirectorName(d.directorName || "");
          setDirectorEmail(d.directorEmail || "");
          setDirectorPhone(d.directorPhone || "");
          setDirectorFax(d.directorFax || "");
        }
      })
      .catch((ex) => Common.LogError(ex, "FacilityInfoTab", "loadFacilityInfo"))
      .finally(() => setIsLoading(false));
  }, [facilityId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "--";
    const d = new Date(dateStr);
    if (isNaN(d)) return "--";
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  };

  const cmnSpecialistDisplay = () => {
    if (!info) return "--";
    const n =
      info.cmnSpecialistName ||
      (info.woundAndProductSpecialist && info.woundAndProductSpecialist.name) ||
      "";
    return Common.replaceNullOrEmptyWithString(n, "--");
  };

  const handleSave = () => {
    setIsSaving(true);
    const payload = {
      notes: notes || null,
      docuSignName: docuSignName || null,
      docuSignEmail: docuSignEmail || null,
      deliveryMethodID: deliveryMethodId ? Number(deliveryMethodId) : null,
      frequencyID: frequencyId ? Number(frequencyId) : null,
      alternateSignee1: alternateSignee1 != null ? Number(alternateSignee1) : 0,
      alternateSignee2: alternateSignee2 != null ? Number(alternateSignee2) : 0,
      clinicianFirstName: clinicianFirstName || null,
      clinicianLastName: clinicianLastName || null,
      directorName: directorName || null,
      directorEmail: directorEmail || null,
      directorPhone: directorPhone || null,
      directorFax: directorFax || null,
    };
    Api.patchRequest(Api.Uri_CmnFacilityInfo + facilityId, payload)
      .then((res) => {
        if (res.data.success) {
          Common.showSuccessAlertAutoClose("Facility information updated successfully.", "success", "Success!", 3000);
        } else {
          Common.showSuccessAlert(res.data.message || "Failed to update facility information.", "warning", "Warning!", 3000);
        }
      })
      .catch((ex) => {
        Common.LogError(ex, "FacilityInfoTab", "handleSave");
        Common.showSuccessAlertAutoClose("An error occurred while saving.", "warning", "Failed!", 3000);
      })
      .finally(() => setIsSaving(false));
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!info) return null;

  const signee1Option =
    alternateSignee1 != null ? physicianOptions.find((o) => o.value === alternateSignee1) || null : null;
  const signee2Option =
    alternateSignee2 != null ? physicianOptions.find((o) => o.value === alternateSignee2) || null : null;

  const selectStyles = {
    control: (base) => ({ ...base, minHeight: "31px", fontSize: "0.875rem" }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
  };

  const labelClass = "form-label mb-1";

  return (
    <div className="p-2">
      <fieldset className={fieldsetClass}>
        <legend className="text-primary fw-500 w-auto px-1">Facility information</legend>
        <Row className="g-2">
          <Col xs={12} sm={6} md={4} xl={3}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Facility ownership</Form.Label>
              <Form.Control size="sm" readOnly value={Common.replaceNullOrEmptyWithString(info.organizationName, "--")} />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4} xl={3}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Facility phone number</Form.Label>
              <Form.Control size="sm" readOnly value={Common.replaceNullOrEmptyWithString(info.facilityPhoneNumber, "--")} />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4} xl={3}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Fax number</Form.Label>
              <Form.Control size="sm" readOnly value={Common.replaceNullOrEmptyWithString(info.facilityFax, "--")} />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4} xl={3}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>CMN specialist</Form.Label>
              <Form.Control size="sm" readOnly value={cmnSpecialistDisplay()} />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4} xl={3}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Clinician first name</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={clinicianFirstName}
                onChange={(e) => setClinicianFirstName(e.target.value)}
                maxLength={100}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4} xl={3}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Clinician last name</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={clinicianLastName}
                onChange={(e) => setClinicianLastName(e.target.value)}
                maxLength={100}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4} xl={3}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Alternate signee 1</Form.Label>
              <Select
                options={physicianOptions}
                value={signee1Option}
                onChange={(opt) => setAlternateSignee1(opt ? opt.value : null)}
                isClearable
                placeholder="Search PECOS physician..."
                noOptionsMessage={() => "No PECOS-certified physicians found"}
                styles={selectStyles}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4} xl={3}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Alternate signee 2</Form.Label>
              <Select
                options={physicianOptions}
                value={signee2Option}
                onChange={(opt) => setAlternateSignee2(opt ? opt.value : null)}
                isClearable
                placeholder="Search PECOS physician..."
                noOptionsMessage={() => "No PECOS-certified physicians found"}
                styles={selectStyles}
              />
            </Form.Group>
          </Col>
        </Row>
      </fieldset>

      <fieldset className={fieldsetClass}>
        <legend className="text-primary fw-500 w-auto px-1">DocuSign &amp; delivery</legend>
        <Row className="g-2">
          <Col xs={12} md={6}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                size="sm"
                placeholder="Notes..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={4000}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Row className="g-2">
              <Col xs={12}>
                <Form.Group className="mb-2">
                  <Form.Label className={labelClass}>DocuSign signee full name</Form.Label>
                  <Form.Control
                    size="sm"
                    type="text"
                    placeholder="Full name for DocuSign"
                    value={docuSignName}
                    onChange={(e) => setDocuSignName(e.target.value)}
                    maxLength={200}
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group className="mb-2">
                  <Form.Label className={labelClass}>DocuSign email</Form.Label>
                  <Form.Control
                    size="sm"
                    type="email"
                    placeholder="Email for DocuSign"
                    value={docuSignEmail}
                    onChange={(e) => setDocuSignEmail(e.target.value)}
                    maxLength={200}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Group className="mb-2">
                  <Form.Label className={labelClass}>CMN delivery method</Form.Label>
                  <Form.Control size="sm" as="select" value={deliveryMethodId} onChange={(e) => setDeliveryMethodId(e.target.value)}>
                    <option value="">-- Select --</option>
                    {deliveryMethodOptions.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Group className="mb-2">
                  <Form.Label className={labelClass}>Frequency</Form.Label>
                  <Form.Control size="sm" as="select" value={frequencyId} onChange={(e) => setFrequencyId(e.target.value)}>
                    <option value="">-- Select --</option>
                    {frequencyOptions.map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col xs={12} sm={6} md={4}>
                <Form.Group className="mb-2">
                  <Form.Label className={labelClass}>Last sync date</Form.Label>
                  <Form.Control size="sm" readOnly value={formatDate(info.lastSyncDate)} />
                </Form.Group>
              </Col>
            </Row>
          </Col>
        </Row>
      </fieldset>

      <fieldset className={fieldsetClass}>
        <legend className="text-primary fw-500 w-auto px-1">Facility medical director</legend>
        <Row className="g-2">
          <Col xs={12} sm={6} md={4} xl={3}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Facility medical director name</Form.Label>
              <Form.Control size="sm" type="text" value={directorName} onChange={(e) => setDirectorName(e.target.value)} maxLength={255} />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4} xl={3}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Facility medical director email</Form.Label>
              <Form.Control
                size="sm"
                type="email"
                value={directorEmail}
                onChange={(e) => setDirectorEmail(e.target.value)}
                maxLength={100}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4} xl={3}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Facility medical director phone</Form.Label>
              <Form.Control size="sm" type="text" value={directorPhone} onChange={(e) => setDirectorPhone(e.target.value)} maxLength={15} />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4} xl={3}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Facility medical director fax</Form.Label>
              <Form.Control size="sm" type="text" value={directorFax} onChange={(e) => setDirectorFax(e.target.value)} maxLength={15} />
            </Form.Group>
          </Col>
        </Row>
      </fieldset>

      <div className="d-flex justify-content-end mt-2">
        <Button variant="primary" size="sm" onClick={handleSave} disabled={isSaving}>
          {isSaving ? <Spinner animation="border" size="sm" /> : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}

export default FacilityInfoTab;
